import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { authAPI } from 'api/AuthAPI'
import axios from 'axios'
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, InteractionType } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { IError } from 'types/error'
import { scopes } from 'api/authConfig'
import { removeNonCaleoStorage } from 'utils/utils'
import { getStorageValue } from './data'
import { ChatContext } from 'Components/General/ChatContext'
import { router } from '../router'
import {
  beginAzureBootstrap,
  clearAuthSession,
  completeAzureBootstrap,
  failAzureBootstrap,
  acquireSilentAzureAccessToken,
  getAuthSessionMode,
  getAzureAccessToken,
  getAzureAccount,
  setAuthSessionMode,
  setAzureAccessToken,
  setAzureAccount,
  setAzureMsalInstance,
} from './authSession'

interface LoginState {
  azureToken: string | null
  userId: number | null
  loggingIn: boolean
  setLoggingIn: (value: boolean) => void
  loggedIn: boolean
  setLoggedIn: (value: boolean) => void
  doLogin: (body: unknown) => Promise<void>
  logout: () => Promise<void>
  azureLogin: () => void
  getAzureUser: () => Promise<boolean>
  error: string | undefined
  loginLoading: boolean
  setResetPassword: (value: boolean) => void
  setLoginLoading: (value: boolean) => void
}

export class LoginError extends Error {}

// The default value's type is a lie here because the default value is
// never actually used! We always give the unexported Provider a valid
// value as a prop
const loginContext = React.createContext<LoginState>({} as LoginState)

const { Provider } = loginContext

/**
 * Provider for handling authentication operations in the application.
 *
 * @param children
 * @returns Provider for handling authentication operations.
 */
export const LoginProvider: React.FC<Record<string, unknown>> = ({ children }) => {
  const { t } = useTranslation()
  const [azureToken, _setAzureToken] = useState<string | null>(null)
  const [userId, setUserId] = useState<number | null>(null)
  const [loggingIn, setLoggingIn] = useState<boolean>(false)
  const [loggedIn, setLoggedIn] = useState<boolean>(false)
  const [error, setError] = useState<string>()
  const [loginLoading, setLoginLoading] = useState<boolean>(() => getAuthSessionMode() !== null)
  const [resetPassword, setResetPassword] = useState<boolean>(false)
  const [doAzureLogin, setDoAzureLogin] = useState<boolean>(false)
  const { instance, inProgress } = useMsal()
  const { isConnected, disconnect } = useContext(ChatContext)
  const hasRestoredAuthSession = useRef(false)

  const rememberLastPath = () => {
    const pathname = router.state.location.pathname
    if (pathname && !pathname.includes('login') && !pathname.includes('logout')) {
      const target = pathname + router.state.location.search
      if (getStorageValue('debug', false)) {
        console.log('rememberLastPath: caching target', target)
      }
      window.sessionStorage.setItem('lastNonLoginPath', target)
    }
  }

  const removeMsalStorage = () => {
    if (getStorageValue('debug', false)) {
      console.log('remove msal storage')
    }

    const items = { ...localStorage }

    for (const key of Object.keys(items)) {
      if (key.startsWith('msal')) {
        localStorage.removeItem(key)
      }
      if (key.includes('login.windows.net')) {
        localStorage.removeItem(key)
      }
      if (key === 'accountId') {
        localStorage.removeItem(key)
      }
      if (key.startsWith('server-telemetry')) {
        localStorage.removeItem(key)
      }
    }
  }

  const getAzureUser = useCallback(async (accessToken?: string): Promise<boolean> => {
    try {
      const data = await authAPI.getUserId(accessToken)

      if (data.activated === false) {
        setLoginLoading(false)
        router.navigate('/activate', {
          state: {
            termsNotAccepted: true,
            loginDetails: { azure: true },
            userData: {
              userId: data.userId,
            },
          },
        })
      } else {
        setUserId(data.userId)
      }

      return true
    } catch (error) {
      const err = error as IError
      console.error(err)
      clearAuthSession()
      removeNonCaleoStorage()
      setError(err.key)
      setLoggingIn(false)
      setLoginLoading(false)
      setLoggedIn(false)
      return false
    } finally {
      setLoginLoading(false)
    }
  }, [])

  useEffect(() => {
    setAzureMsalInstance(instance)

    const callbackId = instance.addEventCallback(async (event: EventMessage) => {
      if (!resetPassword) {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          setLoginLoading(true)
          const payload = event.payload as AuthenticationResult
          instance.setActiveAccount(payload.account)
          setAzureAccount(payload.account)
          _setAzureToken(payload.accessToken)
          setAuthSessionMode('azure')
          setAzureAccessToken(payload.accessToken)
          beginAzureBootstrap()
          if (await getAzureUser(payload.accessToken)) {
            completeAzureBootstrap()
          } else {
            failAzureBootstrap(new Error('Azure bootstrap failed'))
          }
          setLoggingIn(false)
          if (!error) setLoggedIn(true)
          setLoginLoading(false)
          return
        }

        if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
          setLoginLoading(true)
          const payload = event.payload as AuthenticationResult
          instance.setActiveAccount(payload.account)
          setAzureAccount(payload.account)
          _setAzureToken(payload.accessToken)
          setAuthSessionMode('azure')
          setAzureAccessToken(payload.accessToken)
          beginAzureBootstrap()
          if (await getAzureUser(payload.accessToken)) {
            completeAzureBootstrap()
          } else {
            failAzureBootstrap(new Error('Azure bootstrap failed'))
          }
          setLoggingIn(false)
          if (!error) setLoggedIn(true)
          setLoginLoading(false)
          return
        }

        if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE && event.interactionType === InteractionType.Silent) {
          removeNonCaleoStorage()
          setLoginLoading(false)
          setLoggedIn(false)
          setLoggingIn(false)
          clearAuthSession()
          if (event.error?.name === 'InteractionRequiredAuthError') {
            setLoginLoading(true)
            removeNonCaleoStorage()
            rememberLastPath()
            await instance.loginRedirect({ scopes: scopes, redirectUri: '/login' })
            setLoginLoading(false)
          }
          return
        }

        if (event.eventType === EventType.SSO_SILENT_SUCCESS && event.payload) {
          setLoginLoading(true)
          const payload = event.payload as AuthenticationResult
          instance.setActiveAccount(payload.account)
          setAzureAccount(payload.account)
          _setAzureToken(payload.accessToken)
          setAuthSessionMode('azure')
          setAzureAccessToken(payload.accessToken)
          beginAzureBootstrap()
          if (await getAzureUser(payload.accessToken)) {
            completeAzureBootstrap()
          } else {
            failAzureBootstrap(new Error('Azure bootstrap failed'))
          }
          setLoggingIn(false)
          if (!error) setLoggedIn(true)
          setLoginLoading(false)
          return
        }

        if (event.eventType === EventType.SSO_SILENT_FAILURE) {
          _setAzureToken(null)
          setLoggedIn(false)
          setLoggingIn(false)
          setUserId(null)
          setLoginLoading(false)
          clearAuthSession()
          removeNonCaleoStorage()
          rememberLastPath()
          router.navigate('/login')
          return
        }

        if (
          (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE && event.interactionType !== InteractionType.Silent) ||
          event.eventType === EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE
        ) {
          _setAzureToken(null)
          setLoggedIn(false)
          setLoggingIn(false)
          setUserId(null)
          setLoginLoading(false)
          clearAuthSession()
          removeNonCaleoStorage()
          rememberLastPath()
          router.navigate('/login')
          return
        }

        // for Azure AD debugging
        if (
          getStorageValue<boolean>('debug', false) === true &&
          event.eventType /*!== EventType.LOGIN_SUCCESS &&
          event.eventType !== EventType.ACQUIRE_TOKEN_SUCCESS &&
          event.eventType !== EventType.ACQUIRE_TOKEN_FAILURE &&
          event.eventType !== EventType.SSO_SILENT_SUCCESS*/
        ) {
          console.info('Azure AD SSO event debug:')
          console.info('event type', event.eventType)
          console.info('event error', event.error)
          console.info('event interactionType', event.interactionType)
          console.info('event payload', event.payload)
        }
      }
    })

    return () => {
      setAzureMsalInstance(null)
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [error, getAzureUser, instance, resetPassword])

  useEffect(() => {
    if (hasRestoredAuthSession.current) {
      return
    }

    const sessionMode = getAuthSessionMode()
    const cachedAccount = instance.getAllAccounts()[0] ?? getAzureAccount()

    if (!sessionMode && !cachedAccount) {
      return
    }

    hasRestoredAuthSession.current = true
    setLoginLoading(true)
    setLoggingIn(true)
    setError(undefined)

    void (async () => {
      try {
        if (cachedAccount) {
          instance.setActiveAccount(cachedAccount)
          setAzureAccount(cachedAccount)
          if (!sessionMode) {
            setAuthSessionMode('azure')
          }
        }

        const restoredAccessToken =
          getAzureAccessToken() ?? (cachedAccount ? await acquireSilentAzureAccessToken() : null)
        const restored = await getAzureUser(restoredAccessToken ?? undefined)

        if (restored) {
          setLoggedIn(true)
        }
      } finally {
        setLoggingIn(false)
        setLoginLoading(false)
      }
    })()
  }, [getAzureUser, instance])

  useEffect(() => {
    if (doAzureLogin && !error && inProgress === InteractionStatus.None) {
      rememberLastPath()
      instance.loginRedirect({ scopes: scopes, redirectUri: '/login' })
      setDoAzureLogin(false)
    }
  }, [doAzureLogin, error, inProgress, instance])

  const doLogin = async (body: unknown): Promise<void> => {
    if (loggingIn) {
      return
    }

    setLoggedIn(false)
    setLoggingIn(true)
    try {
      const response = await authAPI.login(body)
      if (typeof response === 'string') {
        if (response === 'Wrong email or password!') {
          throw new LoginError(t('login.wrongPassword'))
        }
        if (response === 'Account not verified!') {
          throw new LoginError(t('login.notActivated'))
        }
        throw new LoginError(t('login.failed'))
      } else if (response.message === 'Terms and policies not accepted') {
        setAuthSessionMode('local')
        setAzureAccessToken(null)
        router.navigate('/activate', {
          state: {
            termsNotAccepted: true,
            loginDetails: body,
            userData: response,
          },
        })
      } else {
        setAuthSessionMode('local')
        setAzureAccessToken(null)
        setUserId(response.userId)
        setLoggedIn(true)
      }
    } catch (err) {
      if (err instanceof LoginError) {
        throw err
      }

      console.error('Unexpected Error while logging in: ', err)
    } finally {
      setLoggingIn(false)
    }
  }

  const logout = async (): Promise<void> => {
    if (loggingIn) {
      return
    }

    // Clear redirect target when user explicitly logs out
    sessionStorage.removeItem('lastNonLoginPath')
    // Clear cached searchable networks on logout
    sessionStorage.removeItem('caleo_searchable_networks')

    // Disconnect from websocket if connected
    if (isConnected) {
      disconnect()
    }

    if (azureToken) {
      const currentAccount = instance.getActiveAccount()

      try {
        // Persist logout in backend audit log for Azure-only sessions.
        await authAPI.logout()
      } catch (err) {
        // Ignore backend logout failures and continue with client-side logout cleanup.
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          console.error('Azure backend logout failed', err)
        }
      }

      _setAzureToken(null)
      setLoggedIn(false)
      setUserId(null)
      clearAuthSession()
      if (currentAccount) {
        await instance.logoutRedirect({ account: currentAccount })
      } else {
        await instance.logoutRedirect()
      }
      removeNonCaleoStorage()
      router.navigate('/login')
      return
    }

    clearAuthSession()

    try {
      await authAPI.logout()
      _setAzureToken(null)
      setLoggedIn(false)
      setUserId(null)
    } catch (err) {
      removeNonCaleoStorage()
      // Ignore errors caused by the client not being logged in in the first place
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        throw err
      }
    }

    removeNonCaleoStorage()
  }

  const azureLogin = () => {
    if (loggingIn) {
      return
    }

    removeMsalStorage()

    setLoginLoading(true)
    setLoggingIn(true)
    setError(undefined)

    const stateUrl = router.state.location.state && router.state.location.state.url
    if (stateUrl && !stateUrl.includes('login')) {
      window.sessionStorage.setItem('lastNonLoginPath', stateUrl)
    } else {
      rememberLastPath()
    }

    setDoAzureLogin(true)
  }

  return React.createElement(
    Provider,
    {
      value: {
        azureToken,
        userId,
        loggingIn,
        setLoggingIn,
        loggedIn,
        setLoggedIn,
        doLogin,
        logout,
        azureLogin,
        getAzureUser,
        error,
        loginLoading,
        setResetPassword,
        setLoginLoading,
      },
    },
    children as React.ReactNode
  )
}

/**
 * Hook for using LoginProvider
 *
 * @returns Hook variables and functions.
 */
export function useLogin(): LoginState {
  return useContext(loginContext)
}
